import { AloftPrice, schAloftPrice } from '@aloft/toolkit-shared';
import { Block, Flex, SimpleInput, Typography } from '@aloft/toolkit-sui';

import { useEffect } from 'react';
import { Controller, Form, FormProvider, SubmitHandler } from 'react-hook-form';

import { FormTextarea } from '../../../components/FormTextarea/FormTextarea.tsx';
import { useAppraisifyValidation } from '../../../hooks/validation/useAppraisifyValidation.ts';
import { submitReconciliationDataSheet } from '../../../redux/features/reconciliationSlice.ts';
import { selectCommentary } from '../../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectReconciliation } from '../../../redux/features/selectors/selectReconciliation.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { useReconciliationSheetForm } from './hooks/useReconciliationSheetForm.ts';
import { ReconciliationSheetFormOutput } from './schemas.ts';

export const ReconciliationSheet = () => {
  const dispatch = useAppDispatch();
  const form = useReconciliationSheetForm();
  const validation = useAppraisifyValidation();
  const {
    totalCostToCure: totalCostToCureError,
    opinionOfMarketValue: opinionOfMarketValueError,
    reconciliationCommentary: reconciliationCommentaryError,
  } = validation.errors.Reconciliation;
  const { reconciliation: reconciliationCommentary } = useAppSelector((state) =>
    selectCommentary(state),
  );
  const reconciliation = useAppSelector((state) => selectReconciliation(state));

  useEffect(() => {
    form.reset({
      reconciliationCommentary: reconciliationCommentary?.reconciliationCommentary ?? '',
      totalCostToCure: reconciliation.totalCostToCure
        ? schAloftPrice.parse(reconciliation.totalCostToCure)
        : undefined,
      opinionOfMarketValue: reconciliation.opinionOfMarketValue
        ? schAloftPrice.parse(reconciliation.opinionOfMarketValue)
        : undefined,
    });
  }, []);

  const onSubmit: SubmitHandler<ReconciliationSheetFormOutput> = (data) => {
    dispatch(submitReconciliationDataSheet(data));
  };

  const handleChange = () => {
    form
      .handleSubmit(onSubmit, undefined)()
      .catch(() => {});
  };

  return (
    <Flex flexDirection="column" marginLeft="2rem" marginRight="4rem" height="100%">
      <Typography variant="h1">Reconciliation</Typography>
      <FormProvider {...form}>
        <Form onChange={handleChange} style={{ height: '100%' }}>
          <Flex marginTop="20px" gap={4}>
            {reconciliation?.includeTotalCostToCure && (
              <Flex flexDirection="column" flex={1}>
                <Block marginBottom="5px">
                  <Typography variant="h3" color="textSecondary">
                    Total Cost to Cure
                    <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                      *
                    </span>
                  </Typography>
                </Block>
                <Controller
                  name="totalCostToCure"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <SimpleInput
                      type="number"
                      value={field.value ? field.value.toString(true, true) : ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value
                          ? AloftPrice.fromDollarLike(e.target.value)
                          : undefined;
                        field.onChange(value);
                        handleChange();
                      }}
                      onBlur={field.onBlur}
                      precision={0}
                      prefix="$ "
                      useSeparator
                      required
                      placeholder="Required"
                      errorMessage={
                        fieldState.isTouched ? totalCostToCureError?.message : undefined
                      }
                      invalid={fieldState.invalid && fieldState.isTouched}
                    />
                  )}
                />
              </Flex>
            )}
            <Flex flexDirection="column" flex={1}>
              <Block marginBottom="5px">
                <Typography variant="h3" color="textSecondary">
                  Opinion of Market Value
                  <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                    *
                  </span>
                </Typography>
              </Block>
              <Controller
                name="opinionOfMarketValue"
                control={form.control}
                render={({ field, fieldState }) => (
                  <SimpleInput
                    type="number"
                    value={field.value ? field.value.toString(true, true) : ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value
                        ? AloftPrice.fromDollarLike(e.target.value)
                        : undefined;
                      field.onChange(value);
                      handleChange();
                    }}
                    onBlur={field.onBlur}
                    precision={0}
                    prefix="$ "
                    useSeparator
                    required
                    placeholder="Required"
                    errorMessage={
                      fieldState.isTouched ? opinionOfMarketValueError?.message : undefined
                    }
                    invalid={fieldState.invalid && fieldState.isTouched}
                  />
                )}
              />
            </Flex>
          </Flex>
          <Flex marginTop="20px" flexDirection="column" flex={1} height="80%">
            <Controller
              name="reconciliationCommentary"
              control={form.control}
              render={({ fieldState }) => (
                <FormTextarea
                  name="reconciliationCommentary"
                  control={form.control}
                  label="Reconciliation Commentary"
                  placeholder="Required"
                  isRequired
                  column
                  resize="vertical"
                  customErrorMessage={
                    fieldState.isTouched ? reconciliationCommentaryError?.message : undefined
                  }
                />
              )}
            />
          </Flex>
        </Form>
      </FormProvider>
    </Flex>
  );
};
